import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Battery, 
  BatteryCharging, 
  BatteryLow, 
  BatteryWarning,
  Wifi, 
  WifiOff,
  Gauge,
  Activity,
  RefreshCw,
  Radio,
  Droplets,
  AlertTriangle,
  CheckCircle2,
  Circle
} from 'lucide-react';

const TelemetryPage = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [devices, setDevices] = useState({});
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    setReconnecting(true);
    
    // Build WebSocket URL - use /api prefix for proper routing
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, wsProtocol);
    const wsUrl = `${backendUrl}/api/ws/${token}`;
    
    console.log('Connecting to WebSocket...');
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
        setReconnecting(false);
        toast.success('Live telemetry connected / 即時遙測已連接');
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'telemetry') {
            setDevices(prev => ({
              ...prev,
              [message.device_id]: {
                ...message.data,
                source: message.source || 'unknown',  // 'fac_cloud' or 'local'
                lastUpdate: new Date()
              }
            }));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code);
        setConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 3 seconds (unless it was a normal close)
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnected(false);
        setReconnecting(false);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setReconnecting(false);
    }
  }, [token]);

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      // Cleanup on unmount
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [connectWebSocket]);

  const handleReconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    connectWebSocket();
  };

  // Battery icon based on level and charging state
  const getBatteryIcon = (battery) => {
    if (battery.charging) {
      return <BatteryCharging className="w-6 h-6 text-green-400" />;
    }
    if (battery.level < 10) {
      return <BatteryWarning className="w-6 h-6 text-red-500 animate-pulse" />;
    }
    if (battery.level < 20) {
      return <BatteryLow className="w-6 h-6 text-yellow-500" />;
    }
    return <Battery className="w-6 h-6 text-green-400" />;
  };

  // Battery color based on level
  const getBatteryColor = (level) => {
    if (level < 10) return 'bg-red-500';
    if (level < 20) return 'bg-yellow-500';
    if (level < 50) return 'bg-yellow-400';
    return 'bg-green-500';
  };

  // WiFi icon based on quality
  const getWifiIcon = (wifi) => {
    if (!wifi.connected) {
      return <WifiOff className="w-6 h-6 text-red-500" />;
    }
    return <Wifi className="w-6 h-6 text-green-400" />;
  };

  // WiFi signal strength bars
  const getSignalBars = (rssi) => {
    let bars = 1;
    if (rssi >= -50) bars = 4;
    else if (rssi >= -60) bars = 3;
    else if (rssi >= -70) bars = 2;
    return bars;
  };

  // Pressure status color
  const getPressureColor = (status) => {
    switch (status) {
      case 'critical': return 'text-red-500';
      case 'high': return 'text-orange-500';
      case 'low': return 'text-yellow-500';
      default: return 'text-green-400';
    }
  };

  const deviceList = Object.entries(devices);

  return (
    <div className="min-h-screen bg-fac-dark p-4 pb-24" data-testid="telemetry-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white font-heading">
            Live Telemetry
          </h1>
          <p className="text-muted-foreground font-chinese">即時遙測數據</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                <Circle className="w-2 h-2 mr-1 fill-green-400" />
                Live
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/50">
                <Circle className="w-2 h-2 mr-1 fill-red-400" />
                Offline
              </Badge>
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleReconnect}
            disabled={reconnecting}
            className="border-border text-white hover:bg-fac-green/20"
            data-testid="reconnect-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${reconnecting ? 'animate-spin' : ''}`} />
            {reconnecting ? 'Connecting...' : 'Reconnect'}
          </Button>
        </div>
      </div>

      {/* No Devices Message */}
      {deviceList.length === 0 && (
        <Card className="bg-fac-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Radio className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-white text-lg font-medium mb-1">
              {connected ? 'Waiting for telemetry data...' : 'Connecting to live feed...'}
            </p>
            <p className="text-muted-foreground font-chinese">
              {connected ? '等待遙測數據中...' : '正在連接即時數據...'}
            </p>
            {!connected && (
              <Button
                onClick={handleReconnect}
                className="mt-4 bg-fac-green hover:bg-fac-green/90 text-black"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Connect Now
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Device Telemetry Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {deviceList.map(([deviceId, data]) => (
          <Card key={deviceId} className="bg-fac-card border-border overflow-hidden" data-testid={`device-card-${deviceId}`}>
            {/* Device Header */}
            <CardHeader className="pb-2 border-b border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-fac-green" />
                  <CardTitle className="text-lg text-white">
                    {data.device?.name || 'Device'}
                  </CardTitle>
                </div>
                <Badge 
                  className={`${data.device?.status === 'online' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/50' 
                    : 'bg-red-500/20 text-red-400 border-red-500/50'}`}
                >
                  {data.device?.status === 'online' ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Online</>
                  ) : (
                    <><AlertTriangle className="w-3 h-3 mr-1" /> Offline</>
                  )}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Valve: <span className={data.device?.valve_state === 'open' ? 'text-green-400' : 'text-gray-400'}>
                  {data.device?.valve_state === 'open' ? 'OPEN 開啟' : 'CLOSED 關閉'}
                </span>
              </p>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* Battery Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getBatteryIcon(data.battery)}
                  <span className="text-white font-medium">Battery 電池</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-3 bg-fac-dark rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getBatteryColor(data.battery?.level)} transition-all duration-500`}
                      style={{ width: `${data.battery?.level || 0}%` }}
                    />
                  </div>
                  <span className="text-white font-mono text-sm w-12 text-right">
                    {Math.round(data.battery?.level || 0)}%
                  </span>
                </div>
              </div>
              {data.battery?.charging && (
                <p className="text-xs text-green-400 ml-8">⚡ Charging 充電中</p>
              )}

              {/* Pressure Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gauge className={`w-6 h-6 ${getPressureColor(data.pressure?.status)}`} />
                  <span className="text-white font-medium">Pressure 水壓</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-lg ${getPressureColor(data.pressure?.status)}`}>
                    {data.pressure?.current?.toFixed(1) || '0.0'}
                  </span>
                  <span className="text-muted-foreground text-sm">bar</span>
                </div>
              </div>
              
              {/* Pressure Mini Chart */}
              {data.pressure?.history && data.pressure.history.length > 0 && (
                <div className="h-12 flex items-end gap-0.5 bg-fac-dark/50 rounded p-1">
                  {data.pressure.history.slice(-20).map((point, idx) => {
                    const height = Math.max(10, Math.min(100, (point.value / 6) * 100));
                    return (
                      <div
                        key={idx}
                        className="flex-1 bg-fac-green/70 rounded-t transition-all duration-300"
                        style={{ height: `${height}%` }}
                        title={`${point.value} bar`}
                      />
                    );
                  })}
                </div>
              )}

              {/* WiFi Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getWifiIcon(data.wifi)}
                  <span className="text-white font-medium">Signal 訊號</span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Signal Bars */}
                  <div className="flex items-end gap-0.5 h-4">
                    {[1, 2, 3, 4].map((bar) => (
                      <div
                        key={bar}
                        className={`w-1.5 rounded-sm transition-all ${
                          bar <= getSignalBars(data.wifi?.rssi || -90)
                            ? 'bg-green-400'
                            : 'bg-gray-600'
                        }`}
                        style={{ height: `${bar * 25}%` }}
                      />
                    ))}
                  </div>
                  <span className="text-muted-foreground text-sm font-mono">
                    {data.wifi?.rssi || '--'} dBm
                  </span>
                </div>
              </div>
              
              {/* WiFi Quality Badge */}
              <div className="flex justify-end">
                <Badge variant="outline" className={`text-xs ${
                  data.wifi?.quality === 'excellent' ? 'border-green-500 text-green-400' :
                  data.wifi?.quality === 'good' ? 'border-green-400 text-green-400' :
                  data.wifi?.quality === 'fair' ? 'border-yellow-500 text-yellow-400' :
                  data.wifi?.quality === 'weak' ? 'border-orange-500 text-orange-400' :
                  'border-red-500 text-red-400'
                }`}>
                  {data.wifi?.quality || 'unknown'} 
                  {data.wifi?.quality === 'excellent' && ' 優秀'}
                  {data.wifi?.quality === 'good' && ' 良好'}
                  {data.wifi?.quality === 'fair' && ' 普通'}
                  {data.wifi?.quality === 'weak' && ' 弱'}
                  {data.wifi?.quality === 'poor' && ' 差'}
                </Badge>
              </div>

              {/* Last Update */}
              <div className="pt-2 border-t border-border/50 flex items-center justify-between">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  Last update: {data.lastUpdate ? new Date(data.lastUpdate).toLocaleTimeString() : '--'}
                </p>
                {/* Data Source Indicator */}
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    data.source === 'fac_cloud' 
                      ? 'border-blue-500/50 text-blue-400' 
                      : 'border-gray-500/50 text-gray-400'
                  }`}
                >
                  {data.source === 'fac_cloud' ? '📡 Live' : '💾 Local'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Legend */}
      {deviceList.length > 0 && (
        <div className="mt-6 p-4 bg-fac-card border border-border rounded-lg">
          <h3 className="text-sm font-medium text-white mb-3">Legend 圖例</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span>Good 良好</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-500" />
              <span>Warning 警告</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-orange-500" />
              <span>High 偏高</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span>Critical 危急</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelemetryPage;
